import React, { useState, useEffect } from 'react';
import { useRooms } from '../context/RoomContext';
import { useNavigate } from 'react-router-dom';
import { format, addDays, differenceInDays, parseISO, isAfter, isSameDay, startOfDay, endOfDay } from 'date-fns';
import { Calendar, Phone, Mail, User, AlertCircle, Users, Check, Clock, X, ChevronLeft, ChevronRight } from 'lucide-react';
import BookingConfirmationModal from '../components/BookingConfirmationModal';
import BookingConfirmationPopup from '../components/BookingConfirmationPopup';

const RoomsPage: React.FC = () => {
  const { rooms, bookRoom, hasDateConflict, getBookedDatesForRoom, isRoomBookable } = useRooms();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    checkIn: format(new Date(), 'yyyy-MM-dd'),
    checkOut: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    selectedRoom: '',
    userNotes: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCalendar, setShowCalendar] = useState<'checkIn' | 'checkOut' | null>(null);
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(new Date().getMonth());
  const [currentCalendarYear, setCurrentCalendarYear] = useState(new Date().getFullYear());
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingDetails, setBookingDetails] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter out unavailable rooms
  const availableRooms = rooms.filter(room => room.available);

  useEffect(() => {
    if (availableRooms.length > 0 && !formData.selectedRoom) {
      setFormData(prev => ({
        ...prev,
        selectedRoom: availableRooms[0].id
      }));
    }
  }, [availableRooms]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      // Validate dates when room, check-in, or check-out changes
      if (name === 'selectedRoom' || name === 'checkIn' || name === 'checkOut') {
        if (newData.selectedRoom && newData.checkIn && newData.checkOut) {
          const checkInDate = parseISO(newData.checkIn);
          const checkOutDate = parseISO(newData.checkOut);
          
          // Check if room is bookable
          if (!isRoomBookable(newData.selectedRoom)) {
            setError('This room is currently not available for booking');
            return newData;
          }
          
          // Check if check-out is after check-in
          if (!isAfter(checkOutDate, checkInDate)) {
            setError('Check-out date must be after check-in date');
            return newData;
          }

          // Check if dates have conflicts (12 PM to 12 PM system)
          if (hasDateConflict(newData.selectedRoom, newData.checkIn, newData.checkOut)) {
            setError('Selected dates are not available for this room. Please choose different dates.');
          } else {
            setError('');
          }
        }
      }

      return newData;
    });
    
    // Clear error when user starts typing
    if (name !== 'selectedRoom' && name !== 'checkIn' && name !== 'checkOut') {
      setError('');
    }
  };

  const handleDateSelect = (date: string, type: 'checkIn' | 'checkOut') => {
    const newData = { ...formData, [type]: date };
    
    if (type === 'checkIn') {
      // Auto-adjust checkout if it's not after the new check-in
      const checkInDate = parseISO(date);
      const checkOutDate = parseISO(newData.checkOut);
      
      if (!isAfter(checkOutDate, checkInDate)) {
        newData.checkOut = format(addDays(checkInDate, 1), 'yyyy-MM-dd');
      }
    }

    setFormData(newData);
    setShowCalendar(null);

    // Validate the new dates
    if (newData.selectedRoom && newData.checkIn && newData.checkOut) {
      const checkInDate = parseISO(newData.checkIn);
      const checkOutDate = parseISO(newData.checkOut);
      
      // Check if room is bookable
      if (!isRoomBookable(newData.selectedRoom)) {
        setError('This room is currently not available for booking');
        return;
      }
      
      if (hasDateConflict(newData.selectedRoom, newData.checkIn, newData.checkOut)) {
        setError('Selected dates are not available for this room. Please choose different dates.');
      } else {
        setError('');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.selectedRoom) {
      setError('Please select a room');
      return;
    }

    const selectedRoom = availableRooms.find(room => room.id === formData.selectedRoom);
    if (!selectedRoom) {
      setError('Invalid room selection');
      return;
    }

    // Check if room is bookable
    if (!isRoomBookable(formData.selectedRoom)) {
      setError('This room is currently not available for booking');
      return;
    }

    const checkInDate = parseISO(formData.checkIn);
    const checkOutDate = parseISO(formData.checkOut);
    
    if (!isAfter(checkOutDate, checkInDate)) {
      setError('Check-out date must be after check-in date');
      return;
    }

    // Final availability check before showing confirmation
    if (hasDateConflict(formData.selectedRoom, formData.checkIn, formData.checkOut)) {
      setError('Selected dates are not available for this room. Please choose different dates.');
      return;
    }

    const nights = differenceInDays(checkOutDate, checkInDate);
    const totalPrice = selectedRoom.price * nights;

    // Set booking details for confirmation popup
    setBookingDetails({
      userName: formData.name,
      userEmail: formData.email,
      roomName: selectedRoom.name,
      roomImage: selectedRoom.image_url,
      checkIn: formData.checkIn,
      checkOut: formData.checkOut,
      totalPrice,
      nights,
      pricePerNight: selectedRoom.price,
      source: 'Website'
    });

    // Show confirmation popup
    setShowConfirmationPopup(true);
  };

  const handleConfirmBooking = async () => {
    setIsSubmitting(true);

    try {
      const selectedRoom = availableRooms.find(room => room.id === formData.selectedRoom);
      if (!selectedRoom) {
        throw new Error('Invalid room selection');
      }

      const checkInDate = parseISO(formData.checkIn);
      const checkOutDate = parseISO(formData.checkOut);
      const nights = differenceInDays(checkOutDate, checkInDate);
      const totalPrice = selectedRoom.price * nights;

      await bookRoom({
        roomId: formData.selectedRoom,
        userId: null,
        userName: formData.name,
        userEmail: formData.email,
        userPhone: formData.phone,
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
        totalPrice,
        source: 'Website',
        userNotes: formData.userNotes
      });

      // Close confirmation popup and show success modal
      setShowConfirmationPopup(false);
      setShowSuccessModal(true);

      // Reset form
      setFormData({
        name: '',
        phone: '',
        email: '',
        checkIn: format(new Date(), 'yyyy-MM-dd'),
        checkOut: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
        selectedRoom: availableRooms[0]?.id || '',
        userNotes: '',
      });

    } catch (error) {
      console.error('Booking error:', error);
      setError(error instanceof Error ? error.message : 'Failed to create booking. Please try again.');
      setShowConfirmationPopup(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmationPopup(false);
    setBookingDetails(null);
  };

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
    setBookingDetails(null);
  };

  const handleGoHome = () => {
    setShowSuccessModal(false);
    setBookingDetails(null);
    navigate('/');
  };

  const bookedDates = formData.selectedRoom ? getBookedDatesForRoom(formData.selectedRoom) : [];

  // Check if a specific date is unavailable
  const isDateUnavailable = (date: string) => {
    return bookedDates.includes(date);
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    if (currentCalendarMonth === 0) {
      setCurrentCalendarMonth(11);
      setCurrentCalendarYear(currentCalendarYear - 1);
    } else {
      setCurrentCalendarMonth(currentCalendarMonth - 1);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    if (currentCalendarMonth === 11) {
      setCurrentCalendarMonth(0);
      setCurrentCalendarYear(currentCalendarYear + 1);
    } else {
      setCurrentCalendarMonth(currentCalendarMonth + 1);
    }
  };

  // Generate calendar days for a specific month
  const generateCalendarDays = (month: number, year: number) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const renderCalendar = (type: 'checkIn' | 'checkOut') => {
    const currentDate = new Date();
    const calendarDays = generateCalendarDays(currentCalendarMonth, currentCalendarYear);
    const today = format(currentDate, 'yyyy-MM-dd');
    const displayDate = new Date(currentCalendarYear, currentCalendarMonth, 1);
    
    return (
      <div className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-80">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            Select {type === 'checkIn' ? 'Check-in' : 'Check-out'} Date
          </h3>
          <button
            onClick={() => setShowCalendar(null)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={goToPreviousMonth}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h4 className="font-medium">{format(displayDate, 'MMMM yyyy')}</h4>
          <button
            onClick={goToNextMonth}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day, index) => {
            if (!day) {
              return <div key={index} className="h-8"></div>;
            }
            
            const dateStr = format(day, 'yyyy-MM-dd');
            const isToday = dateStr === today;
            const isPast = day < startOfDay(currentDate);
            const isUnavailable = isDateUnavailable(dateStr);
            const isSelected = dateStr === formData[type];
            
            // For check-out, disable dates that are not after check-in
            const isInvalidCheckOut = type === 'checkOut' && !isAfter(day, parseISO(formData.checkIn));
            
            const isDisabled = isPast || isUnavailable || isInvalidCheckOut;
            
            return (
              <button
                key={index}
                onClick={() => !isDisabled && handleDateSelect(dateStr, type)}
                disabled={isDisabled}
                className={`
                  h-8 w-8 text-sm rounded-md transition-colors
                  ${isSelected 
                    ? 'bg-indigo-600 text-white' 
                    : isToday 
                    ? 'bg-indigo-100 text-indigo-600 font-medium'
                    : isDisabled
                    ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                    : 'hover:bg-indigo-50 text-gray-700'
                  }
                  ${isUnavailable && !isSelected ? 'bg-red-50 text-red-400 line-through' : ''}
                `}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>
        
        <div className="mt-4 text-xs text-gray-500">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-50 border border-red-200 rounded mr-1"></div>
              <span>Unavailable</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-indigo-100 rounded mr-1"></div>
              <span>Today</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-indigo-600 rounded mr-1"></div>
              <span>Selected</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Book Your Stay</h1>
              <div className="mt-2 flex items-center text-sm text-gray-600">
                <Clock className="h-4 w-4 mr-1" />
                <span>Check-in: 12:00 PM | Check-out: 12:00 PM</span>
              </div>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 p-4 rounded-md flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-4 bg-green-50 p-4 rounded-md">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            )}

            {availableRooms.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No rooms available</h3>
                <p className="mt-1 text-sm text-gray-500">All rooms are currently unavailable for booking. Please check back later.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Select a Room</h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {availableRooms.map((room) => (
                      <div
                        key={room.id}
                        className={`relative border rounded-lg overflow-hidden cursor-pointer transition-all ${
                          formData.selectedRoom === room.id
                            ? 'ring-2 ring-indigo-500'
                            : 'hover:border-indigo-500'
                        }`}
                        onClick={() => setFormData(prev => ({ ...prev, selectedRoom: room.id }))}
                      >
                        <img
                          src={room.image_url}
                          alt={room.name}
                          className="w-full h-48 object-cover"
                        />
                        <div className="p-4">
                          <h3 className="text-lg font-medium text-gray-900">{room.name}</h3>
                          <p className="mt-1 text-sm text-gray-500">{room.description}</p>
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <Users className="h-4 w-4 mr-1" />
                            <span>Up to {room.capacity} guests</span>
                          </div>
                          <div className="mt-2 text-lg font-bold text-indigo-600">
                            ${room.price}/night
                          </div>
                        </div>
                        {formData.selectedRoom === room.id && (
                          <div className="absolute top-2 right-2 bg-indigo-500 rounded-full p-1">
                            <Check className="h-4 w-4 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="relative">
                    <label htmlFor="checkIn" className="block text-sm font-medium text-gray-700">
                      Check-in Date (12:00 PM)
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="date"
                        name="checkIn"
                        id="checkIn"
                        required
                        min={format(new Date(), 'yyyy-MM-dd')}
                        className={`form-input ${
                          isDateUnavailable(formData.checkIn) ? 'border-red-300 bg-red-50' : ''
                        }`}
                        value={formData.checkIn}
                        onChange={handleInputChange}
                        onFocus={() => setShowCalendar('checkIn')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCalendar(showCalendar === 'checkIn' ? null : 'checkIn')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <Calendar className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                    {isDateUnavailable(formData.checkIn) && (
                      <p className="mt-1 text-xs text-red-600">This date is not available</p>
                    )}
                    {showCalendar === 'checkIn' && renderCalendar('checkIn')}
                  </div>

                  <div className="relative">
                    <label htmlFor="checkOut" className="block text-sm font-medium text-gray-700">
                      Check-out Date (12:00 PM)
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="date"
                        name="checkOut"
                        id="checkOut"
                        required
                        min={format(addDays(parseISO(formData.checkIn), 1), 'yyyy-MM-dd')}
                        className={`form-input ${
                          isDateUnavailable(formData.checkOut) ? 'border-red-300 bg-red-50' : ''
                        }`}
                        value={formData.checkOut}
                        onChange={handleInputChange}
                        onFocus={() => setShowCalendar('checkOut')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCalendar(showCalendar === 'checkOut' ? null : 'checkOut')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <Calendar className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                    {isDateUnavailable(formData.checkOut) && (
                      <p className="mt-1 text-xs text-red-600">This date is not available</p>
                    )}
                    {showCalendar === 'checkOut' && renderCalendar('checkOut')}
                  </div>
                </div>

                {/* Show unavailable dates for selected room */}
                {formData.selectedRoom && bookedDates.length > 0 && (
                  <div className="bg-amber-50 p-4 rounded-md">
                    <h3 className="text-sm font-medium text-amber-800 mb-2">Unavailable Dates for Selected Room:</h3>
                    <div className="flex flex-wrap gap-2">
                      {bookedDates.slice(0, 10).map(date => (
                        <span key={date} className="inline-block px-2 py-1 text-xs bg-amber-200 text-amber-800 rounded">
                          {format(parseISO(date), 'MMM d')}
                        </span>
                      ))}
                      {bookedDates.length > 10 && (
                        <span className="inline-block px-2 py-1 text-xs bg-amber-200 text-amber-800 rounded">
                          +{bookedDates.length - 10} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      className="form-input-with-icon"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      required
                      className="form-input-with-icon"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      className="form-input-with-icon"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="userNotes" className="block text-sm font-medium text-gray-700">
                    Special Requests or Notes (Optional)
                  </label>
                  <div className="mt-1">
                    <textarea
                      name="userNotes"
                      id="userNotes"
                      rows={3}
                      value={formData.userNotes}
                      onChange={handleInputChange}
                      className="form-input min-h-[80px]"
                      placeholder="Any special requests, dietary requirements, or additional information..."
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Let us know about any special requirements or preferences for your stay.
                  </p>
                </div>

                {formData.selectedRoom && (
                  <div className="bg-gray-50 p-4 rounded-md">
                    {availableRooms.map((room) => {
                      if (room.id === formData.selectedRoom) {
                        const checkInDate = parseISO(formData.checkIn);
                        const checkOutDate = parseISO(formData.checkOut);
                        const nights = isAfter(checkOutDate, checkInDate) 
                          ? differenceInDays(checkOutDate, checkInDate)
                          : 0;
                        const totalPrice = room.price * nights;
                        
                        return (
                          <div key={room.id} className="flex items-start space-x-4">
                            <img
                              src={room.image_url}
                              alt={room.name}
                              className="h-20 w-20 object-cover rounded-md"
                            />
                            <div>
                              <h3 className="text-lg font-medium text-gray-900">{room.name}</h3>
                              <p className="text-sm text-gray-500">{room.description}</p>
                              {nights > 0 && (
                                <div className="mt-2">
                                  <span className="text-sm font-medium text-gray-500">
                                    ${room.price} × {nights} nights =
                                  </span>
                                  <span className="ml-1 text-lg font-bold text-indigo-600">
                                    ${totalPrice}
                                  </span>
                                </div>
                              )}
                              <div className="mt-2 text-xs text-gray-500">
                                <div className="flex items-center">
                                  <Clock className="h-3 w-3 mr-1" />
                                  <span>Check-in: {format(checkInDate, 'MMM d')} at 12:00 PM</span>
                                </div>
                                <div className="flex items-center mt-1">
                                  <Clock className="h-3 w-3 mr-1" />
                                  <span>Check-out: {format(checkOutDate, 'MMM d')} at 12:00 PM</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!!error || !formData.selectedRoom}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Review Booking
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Booking Confirmation Popup */}
      {bookingDetails && (
        <BookingConfirmationPopup
          isOpen={showConfirmationPopup}
          onClose={handleCloseConfirmation}
          onConfirm={handleConfirmBooking}
          bookingDetails={bookingDetails}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Success Modal */}
      {bookingDetails && (
        <BookingConfirmationModal
          isOpen={showSuccessModal}
          onClose={handleCloseSuccess}
          bookingDetails={bookingDetails}
          onGoHome={handleGoHome}
        />
      )}
    </div>
  );
};

export default RoomsPage;